import { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { SearchBar } from '../../markets/components/SearchBar';
import type { NavSection, NavSectionId } from '../navSections';
import {
    SAFE_AREA_LEFT,
    SAFE_AREA_RIGHT,
    SEARCH_ROW_HEIGHT_PX,
    TOP_BAR_HEIGHT_PX,
    phoneResultsMaxHeight,
    phoneSurface,
} from '../chrome';
import { useHideOnScroll } from '../useHideOnScroll';
import { useVisualViewport } from '../useVisualViewport';
import { DOCK_ATTRIBUTE } from '../useEditingOutsideDock';
import { BottomNav } from './BottomNav';
import { AdvancedSearchIcon } from './icons';

// Search mode lasts as long as the field has focus; leaving it ends the mode.
const endSearch = () => (document.activeElement as HTMLElement | null)?.blur();

interface MobileSearchDockProps {
    sections: NavSection[];
    activeId: NavSectionId | null;
    showTabBar: boolean;
    showSearchRow: boolean;
    /**
     * Not shown (display: none), but still mounted: the search query survives.
     * Set while the user types in a field outside the dock.
     */
    hidden?: boolean;
    moreOpen: boolean;
    /**
     * The More sheet is still on screen: open, or closed and sliding out. The
     * dock stays above it until it is gone. Defaults to `moreOpen`.
     */
    sheetShown?: boolean;
    onMore: () => void;
    advancedHref?: string;
}

// The bottom of the phone screen: the search field above the tab bar. While
// searching, the tab bar steps aside, the field rides on top of the keyboard
// and the results fill the screen above it.
export function MobileSearchDock({
    sections,
    activeId,
    showTabBar,
    showSearchRow,
    hidden = false,
    moreOpen,
    sheetShown = moreOpen,
    onMore,
    advancedHref,
}: MobileSearchDockProps) {
    const theme = useTheme();
    const [focused, setFocused] = useState(false);
    // The field and Cancel: Tab between them is not leaving the search.
    const fieldRowRef = useRef<HTMLDivElement>(null);
    const scrolledAway = useHideOnScroll();
    const { height, keyboardInset } = useVisualViewport();
    // Search mode needs a search field to be searching in. When the row goes
    // away with focus still in it (Advanced search opens /markets/search),
    // the field is gone and nothing would end the mode: the scrim would cover
    // the page and the tab bar stay hidden.
    const searching = focused && showSearchRow;
    useEffect(() => {
        if (!showSearchRow) setFocused(false);
    }, [showSearchRow]);
    // The More sheet sits on the tab bar, where the search row was: the row
    // gives way while the sheet is on screen (also while it slides out: the
    // dock is above it then, and the row would show over its last pixels).
    // Without a tab bar the sheet covers the row.
    const onTabBar = (moreOpen || sheetShown) && showTabBar;
    const rowVisible =
        showSearchRow && !onTabBar && (searching || !scrolledAway);
    const resultsMaxHeight = phoneResultsMaxHeight(height);

    // Nothing to dock: no search field and no tab bar. Not even the empty bar.
    if (!showSearchRow && !showTabBar) return null;

    return (
        <>
            {searching && (
                <Box
                    aria-hidden
                    data-testid="search-scrim"
                    // iOS does not blur a field when the tap lands on
                    // content that is not interactive, so a tap on the scrim
                    // ends the search here, like Cancel.
                    onClick={endSearch}
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        top: `calc(${TOP_BAR_HEIGHT_PX}px + env(safe-area-inset-top, 0px))`,
                        bgcolor: phoneSurface(theme),
                        // The app bar's layer, not below it: the chat views
                        // sit at appBar - 1 and must not paint over the scrim.
                        zIndex: theme.zIndex.appBar,
                    }}
                />
            )}
            <Box
                data-testid="search-dock"
                {...{ [DOCK_ATTRIBUTE]: '' }}
                sx={{
                    display: hidden ? 'none' : undefined,
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: searching ? `${keyboardInset}px` : 0,
                    // Above the scrim (the app bar's layer), explicitly. While
                    // the More sheet is on screen on top of the tab bar it is
                    // above the sheet too: the sheet slides in and out behind
                    // the bar (also after More has closed, until it is gone).
                    zIndex: onTabBar
                        ? theme.zIndex.drawer + 1
                        : theme.zIndex.appBar + 1,
                    bgcolor: 'background.default',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.12)',
                    pb: searching ? 0 : 'env(safe-area-inset-bottom, 0px)',
                    // landscape phone: keep the row and the tabs out of the notch
                    pl: SAFE_AREA_LEFT,
                    pr: SAFE_AREA_RIGHT,
                }}
            >
                {/* From 600 px (an iPad held upright) the field and the tabs
                    sit in a 560 px column in the middle; the bar behind them
                    stays full width. */}
                <Box
                    data-testid="search-column"
                    sx={{ maxWidth: { sm: 560 }, mx: 'auto' }}
                >
                    {showSearchRow && (
                        <Box
                            data-testid="search-row"
                            aria-hidden={!rowVisible}
                            sx={{
                                height: rowVisible ? SEARCH_ROW_HEIGHT_PX : 0,
                                // visible, so the results can open above the row
                                overflow: rowVisible ? 'visible' : 'hidden',
                                // Scrolled away: also out of the tab order. The
                                // switch waits for the collapse when hiding.
                                visibility: rowVisible ? 'visible' : 'hidden',
                                transition: rowVisible
                                    ? 'height 160ms ease'
                                    : 'height 160ms ease, visibility 0s linear 160ms',
                            }}
                        >
                            {/* The padding is on this box, not on the one that
                                collapses: a padding would stay behind as a
                                strip above the tab bar. */}
                            <Box
                                ref={fieldRowRef}
                                sx={{
                                    height: SEARCH_ROW_HEIGHT_PX,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    // 8 8 4 12 around the 44 px field, as drawn;
                                    // centred in the row while searching.
                                    pt: searching ? 0 : 1,
                                    pb: searching ? 0 : 0.5,
                                    pl: { xs: 1.5, sm: 0 },
                                    pr: { xs: searching ? 0.5 : 1, sm: 0 },
                                }}
                            >
                                <SearchBar
                                    touch
                                    fullWidth
                                    resultsMaxHeight={resultsMaxHeight}
                                    advancedHref={advancedHref}
                                    onFocusChange={setFocused}
                                    focusScope={fieldRowRef}
                                />
                                {searching ? (
                                    <Button
                                        sx={{
                                            minHeight: 44,
                                            minWidth: 0,
                                            flexShrink: 0,
                                            px: '10px',
                                            fontSize: 15,
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            letterSpacing: 'normal',
                                        }}
                                        onClick={endSearch}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    advancedHref && (
                                        <IconButton
                                            component={Link}
                                            to={advancedHref}
                                            aria-label="Advanced search"
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                flexShrink: 0,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            <AdvancedSearchIcon />
                                        </IconButton>
                                    )
                                )}
                            </Box>
                        </Box>
                    )}
                    {showTabBar && !searching && (
                        <BottomNav
                            sections={sections}
                            activeId={activeId}
                            moreOpen={moreOpen}
                            onMore={onMore}
                        />
                    )}
                </Box>
            </Box>
        </>
    );
}
